import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ChevronDown, ChevronUp, Check, Settings, X } from 'lucide-react';

const CookieBanner = ({ lang = 'de', theme = 'dark', activeTab: initialActiveTab = 'design' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  
  // Custom consent state
  const [consent, setConsent] = useState({
    necessary: true,
    analytics: true,
  });

  // Dynamic branding colors matching LandingPage.jsx CSS variables
  const colors = theme === 'dark' ? {
    design: '#EFF305', // Yellow in dark mode
    ai: '#F41465', // Pink in dark mode
    cre8guide: '#ef4444', // Red
    concierge: '#4f8ef0', // Lighter blue in dark mode
  } : {
    design: '#F41465', // Pink in light mode
    ai: '#F41465', // Pink in light mode
    cre8guide: '#ef4444', // Red
    concierge: '#2563eb', // Electric Blue
  };
  const activeColor = colors[activeTab] || '#F41465';

  useEffect(() => {
    // Sync initial tab state from local storage or URL parameters
    if (typeof window !== 'undefined') {
      const savedTab = localStorage.getItem('cre8now-active-tab');
      if (savedTab) setActiveTab(savedTab);
      
      const handleTabChange = (e) => {
        if (e.detail) {
          setActiveTab(e.detail);
        }
      };

      window.addEventListener('set-landing-tab', handleTabChange);
      window.addEventListener('set_tab', handleTabChange);

      return () => {
        window.removeEventListener('set-landing-tab', handleTabChange);
        window.removeEventListener('set_tab', handleTabChange);
      };
    }
  }, []);

  useEffect(() => {
    // Check if user has already saved their preferences
    const savedConsent = localStorage.getItem('cre8now-cookie-consent');
    if (!savedConsent) {
      // Set Google Consent Mode default (before loaded) - ALL granted by default
      updateGoogleConsent(true);
      // Wait a little before showing banner for clean entry
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      try {
        const parsed = JSON.parse(savedConsent);
        setConsent(parsed);
        // Update Google Consent Mode V2 based on saved preferences
        updateGoogleConsent(parsed.analytics);
      } catch (e) {
        setIsVisible(true);
      }
    }
  }, []);

  const updateGoogleConsent = (analyticsGranted) => {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }

      const consentState = {
        'analytics_storage': analyticsGranted ? 'granted' : 'denied',
        'ad_storage': analyticsGranted ? 'granted' : 'denied', // Fallback matched to Analytics selection since Marketing card is removed
        'ad_user_data': analyticsGranted ? 'granted' : 'denied',
        'ad_personalization': analyticsGranted ? 'granted' : 'denied',
        'personalization_storage': analyticsGranted ? 'granted' : 'denied',
        'functionality_storage': 'granted',
        'security_storage': 'granted'
      };

      // Push consent update or default
      const hasSaved = localStorage.getItem('cre8now-cookie-consent');
      if (hasSaved) {
        gtag('consent', 'update', consentState);
      } else {
        gtag('consent', 'default', consentState);
      }
    }
  };

  const handleAcceptAll = () => {
    const fullConsent = {
      necessary: true,
      analytics: true,
    };
    setConsent(fullConsent);
    localStorage.setItem('cre8now-cookie-consent', JSON.stringify(fullConsent));
    updateGoogleConsent(true);
    setIsVisible(false);
  };

  const handleAcceptNecessary = () => {
    const essentialConsent = {
      necessary: true,
      analytics: false,
    };
    setConsent(essentialConsent);
    localStorage.setItem('cre8now-cookie-consent', JSON.stringify(essentialConsent));
    updateGoogleConsent(false);
    setIsVisible(false);
  };

  const handleSaveCustom = () => {
    localStorage.setItem('cre8now-cookie-consent', JSON.stringify(consent));
    updateGoogleConsent(consent.analytics);
    setIsVisible(false);
  };

  // Translations
  const content = {
    de: {
      title: 'Cookies & Privatsphäre',
      description: 'CRE8NOW verwendet Cookies, um die Nutzererfahrung zu verbessern, Website-Traffic zu analysieren und personalisierte Werbung auszuspielen. Mit deiner Einwilligung hilfst du CRE8NOW, noch besser zu werden.',
      acceptAll: 'Alle akzeptieren',
      essentialOnly: 'Nur Notwendige',
      customize: 'Einstellungen',
      saveSettings: 'Einstellungen speichern',
      back: 'Zurück',
      categories: {
        necessary: {
          title: 'Notwendige Cookies',
          desc: 'Essentiell für das Funktionieren der Website, Sicherheitsfunktionen und Speichern deiner Präferenzen. Können nicht deaktiviert werden.',
        },
        analytics: {
          title: 'Analyse & Statistiken',
          desc: 'Helfen CRE8NOW zu verstehen, wie Besucher mit der Website interagieren (z. B. Google Analytics), um die Usability stetig zu optimieren.',
        },
        marketing: {
          title: 'Marketing & Personalisierung',
          desc: 'Ermöglichen personalisierte Werbekampagnen auf Social Media (z. B. Meta, TikTok, Google Ads) und zielgruppenrelevante Angebote.',
        }
      }
    },
    en: {
      title: 'Cookies & Privacy',
      description: 'CRE8NOW uses cookies to optimize your experience, analyze website traffic, and display personalized campaigns. With your consent, you help CRE8NOW get even better.',
      acceptAll: 'Accept All',
      essentialOnly: 'Essential Only',
      customize: 'Settings',
      saveSettings: 'Save Settings',
      back: 'Back',
      categories: {
        necessary: {
          title: 'Necessary Cookies',
          desc: 'Essential for running the website, security protocols, and saving your baseline settings. Cannot be disabled.',
        },
        analytics: {
          title: 'Analytics & Statistics',
          desc: 'Allows us to measure visitor interaction (e.g. Google Analytics) to continuously optimize site performance and design usability.',
        },
        marketing: {
          title: 'Marketing & Personalization',
          desc: 'Enables personalized advertising on social media networks (e.g. Meta, TikTok, Google Ads) based on relevant interest areas.',
        }
      }
    }
  }[lang] || content.en;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.95 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className={`fixed bottom-6 right-6 z-[9999] w-[calc(100vw-48px)] sm:w-[420px] backdrop-blur-xl border rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden transition-all duration-300 ${
            theme === 'dark'
              ? 'bg-[#0c0c0d]/80 border-white/5 text-white'
              : 'bg-white/85 border-black/5 text-black'
          }`}
        >
          <div className="p-6 md:p-8 space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
              <div 
                className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  theme === 'dark' ? 'bg-white/5' : 'bg-black/5'
                }`}
                style={{ color: activeColor }}
              >
                <Shield size={18} />
              </div>
              <h5 className="text-sm font-black tracking-widest text-text-color font-sans">
                {content.title}
              </h5>
            </div>

            <AnimatePresence mode="wait">
              {!showSettings ? (
                <motion.div
                  key="intro"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  <p 
                    className="text-text-muted leading-relaxed font-light"
                    style={{ fontSize: '13px', lineHeight: '1.5' }}
                  >
                    {content.description}
                  </p>

                  <div className="flex flex-col gap-2 pt-2">
                    <button
                      onClick={handleAcceptAll}
                      style={{ backgroundColor: activeColor }}
                      className={`cta-m w-full py-3.5 rounded-lg text-[10px] font-black uppercase tracking-widest hover:brightness-110 active:scale-[0.98] transition-all duration-300 ${
                        activeTab === 'design' && theme === 'dark' ? 'text-black' : 'text-white'
                      }`}
                    >
                      {content.acceptAll}
                    </button>
                    
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => setShowSettings(true)}
                        className="cta-m w-full py-3.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-border-color hover:bg-text-color/5 text-text-muted hover:text-text-color active:scale-[0.98] transition-all duration-300 flex items-center justify-center"
                      >
                        {content.customize}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="settings"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  {/* Settings toggles */}
                  <div className="space-y-4 max-h-[320px] overflow-y-auto pr-1 custom-scrollbar">
                    {/* Necessary */}
                    <div className="space-y-1.5 p-3 rounded-lg bg-surface-color/30 border border-border-color/30">
                      <div className="flex items-center justify-between">
                        <span className={`text-[10px] font-black uppercase tracking-widest ${activeTab === 'design' && theme !== 'dark' ? 'text-white' : 'text-text-color'}`}>
                          {content.categories.necessary.title}
                        </span>
                        <div className="w-8 h-4 rounded-full bg-text-color/20 flex items-center justify-end px-0.5 opacity-60">
                          <div className="w-3 h-3 rounded-full bg-text-color" />
                        </div>
                      </div>
                      <p 
                        className="text-text-muted leading-relaxed font-light"
                        style={{ fontSize: '13px', lineHeight: '1.5' }}
                      >
                        {content.categories.necessary.desc}
                      </p>
                    </div>

                    {/* Analytics */}
                    <div className="space-y-1.5 p-3 rounded-lg bg-surface-color/30 border border-border-color/30">
                      <div className="flex items-center justify-between">
                        <span className={`text-[10px] font-black uppercase tracking-widest ${activeTab === 'design' && theme !== 'dark' ? 'text-white' : 'text-text-color'}`}>
                          {content.categories.analytics.title}
                        </span>
                        <button
                          type="button"
                          onClick={() => setConsent(prev => ({ ...prev, analytics: !prev.analytics }))}
                          className={`w-9 h-5 rounded-full flex items-center p-0.5 transition-colors duration-300 ${consent.analytics ? '' : 'bg-text-color/10'}`}
                          style={{ backgroundColor: consent.analytics ? activeColor : undefined }}
                        >
                          <motion.div 
                            layout
                            className="w-4 h-4 rounded-full bg-white shadow-md"
                            animate={{ x: consent.analytics ? 16 : 0 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                          />
                        </button>
                      </div>
                      <p 
                        className="text-text-muted leading-relaxed font-light"
                        style={{ fontSize: '13px', lineHeight: '1.5' }}
                      >
                        {content.categories.analytics.desc}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => setShowSettings(false)}
                      className="flex-1 py-3.5 rounded-lg text-[9px] font-black uppercase tracking-widest border border-border-color hover:bg-text-color/5 text-text-muted hover:text-text-color transition-all duration-300"
                    >
                      {content.back}
                    </button>
                    <button
                      onClick={handleSaveCustom}
                      style={{ backgroundColor: activeColor }}
                      className={`flex-1 py-3.5 rounded-lg text-[9px] font-black uppercase tracking-widest hover:brightness-110 active:scale-[0.98] transition-all duration-300 ${
                        activeTab === 'design' && theme === 'dark' ? 'text-black' : 'text-white'
                      }`}
                    >
                      {content.saveSettings}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
